<template>
  <div class="chat-widget-wrap">
    <button class="chat-fab" @click="toggleWidget" :class="{ active: isOpen }">
      <i class="ph-fill" :class="isOpen ? 'ph-x' : 'ph-chat-circle-dots'"></i>
      <span v-if="totalUnread > 0 && !isOpen" class="fab-badge">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
    </button>

    <transition name="chat-panel">
      <div v-if="isOpen" class="chat-panel">

        <!-- ======= 채팅 리스트 ======= -->
        <template v-if="currentView === 'list'">
          <div class="cp-header"><h3>Chat</h3></div>
          <div class="cp-list">
            <div v-if="!chatRooms.length" class="cp-empty">
              <i class="ph ph-chats"></i><p>참여 중인 채팅이 없습니다</p>
            </div>
            <div v-for="room in chatRooms" :key="room.main_group_idx" class="cp-room-item" @click="openRoom(room)">
              <div class="cp-room-icon"><i class="ph-fill ph-users-three"></i></div>
              <div class="cp-room-info">
                <span class="cp-room-name">{{ room.name || '팀 채팅' }}</span>
                <span class="cp-room-last">{{ room.last_msg_type === 'sticker' ? '스티커' : room.last_msg_type === 'image' ? '사진' : room.last_msg_type === 'file' ? '파일' : (room.last_message || '대화를 시작하세요') }}</span>
              </div>
              <div class="cp-room-meta">
                <span class="cp-room-time">{{ formatDate(room.last_time) }}</span>
                <span v-if="room.unread > 0" class="cp-room-badge">{{ room.unread }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- ======= 채팅방 ======= -->
        <template v-if="currentView === 'room'">
          <div class="cp-header room-header">
            <button class="cp-back" @click="goBack"><i class="ph ph-caret-left"></i></button>
            <div class="cp-header-info">
              <span class="cp-header-name">{{ activeRoom?.name || '팀 채팅' }}</span>
              <span class="cp-header-count">{{ teamCount }}명</span>
            </div>
          </div>

          <div class="cp-messages" ref="messagesRef" @scroll="onScroll">
            <div v-if="loading" class="cp-loading">불러오는 중...</div>
            <div
              v-for="(msg, i) in messages" :key="msg.idx || i"
              class="cp-msg" :class="{ mine: msg.mb_id === mbId }"
              @contextmenu.prevent="onMsgContext($event, msg)"
            >
              <img v-if="msg.mb_id !== mbId && (!messages[i-1] || messages[i-1].mb_id !== msg.mb_id)"
                class="cp-msg-avatar" :src="msg.image || '/imgs/unknow_user.png'" :alt="msg.name" />
              <div v-else-if="msg.mb_id !== mbId" class="cp-msg-avatar-spacer"></div>
              <div class="cp-msg-body">
                <span v-if="msg.mb_id !== mbId && (!messages[i-1] || messages[i-1].mb_id !== msg.mb_id)" class="cp-msg-name">{{ msg.name || msg.mb_id }}</span>

                <template v-if="editingIdx !== msg.idx">
                  <div v-if="!msg.msg_type || msg.msg_type === 'text'" class="cp-msg-bubble">{{ msg.message }}</div>
                  <div v-else-if="msg.msg_type === 'sticker'" class="cp-msg-sticker">
                    <img :src="msg.message" alt="sticker" />
                  </div>
                  <div v-else-if="msg.msg_type === 'image'" class="cp-msg-img">
                    <img :src="msg.file_url" :alt="msg.file_name" @click="window.open(msg.file_url,'_blank')" />
                  </div>
                  <div v-else-if="msg.msg_type === 'file'" class="cp-msg-file" @click="window.open(msg.file_url,'_blank')">
                    <i class="ph ph-file-arrow-down"></i>
                    <span>{{ msg.file_name || '파일' }}</span>
                  </div>
                </template>
                <div class="cp-msg-edit-wrap" v-else>
                  <input v-model="editingText" class="cp-msg-edit-input" @keyup.enter="confirmEdit" @keyup.escape="cancelEdit" ref="editInputRef" />
                  <div class="cp-msg-edit-actions">
                    <button @click="confirmEdit"><i class="ph ph-check"></i></button>
                    <button @click="cancelEdit"><i class="ph ph-x"></i></button>
                  </div>
                </div>

                <div class="cp-msg-meta">
                  <span v-if="msg.mb_id === mbId" class="cp-msg-read">{{ getUnreadCount(msg) > 0 ? getUnreadCount(msg) : '' }}</span>
                  <span class="cp-msg-time">{{ formatTime(msg.created_at) }}</span>
                </div>
              </div>
            </div>
            <div v-if="!loading && messages.length === 0" class="cp-empty-msg">
              <i class="ph ph-chats"></i><p>첫 메시지를 보내보세요!</p>
            </div>
          </div>

          <StickerSelector v-model="stickerOpen" @select="sendSticker" />

          <div class="cp-input-area">
            <button class="cp-tool-btn" @click="stickerOpen = !stickerOpen" :class="{ active: stickerOpen }">
              <i class="ph ph-smiley-sticker"></i>
            </button>
            <label class="cp-tool-btn">
              <i class="ph ph-paperclip"></i>
              <input type="file" hidden @change="handleFileUpload" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.hwp" />
            </label>
            <input v-model="inputMsg" placeholder="메시지를 입력하세요..." class="cp-input" @keyup.enter="sendMessage" ref="inputRef" />
            <button class="cp-send" @click="sendMessage" :disabled="!inputMsg.trim()">
              <i class="ph-fill ph-paper-plane-tilt"></i>
            </button>
          </div>
        </template>

        <teleport to="body">
          <transition name="ctx-fade">
            <div v-if="ctxVisible" class="ctx-menu" :style="{ top: ctxY + 'px', left: ctxX + 'px' }">
              <button v-if="ctxMsg && (!ctxMsg.msg_type || ctxMsg.msg_type === 'text')" @click="startEdit"><i class="ph ph-pencil-simple"></i> 수정</button>
              <button class="ctx-danger" @click="deleteMsg"><i class="ph ph-trash"></i> 삭제</button>
            </div>
          </transition>
        </teleport>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, inject, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { io } from 'socket.io-client';
import axios from 'axios';
import StickerSelector from './StickerSelector.vue';

export default {
  components: { StickerSelector },
  setup() {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');
    const member_info = inject('member_info');
    const mbId = getMbid();

    const isOpen = ref(false);
    const currentView = ref('list');
    const chatRooms = ref([]);
    const activeRoom = ref(null);
    const messages = ref([]);
    const inputMsg = ref('');
    const loading = ref(false);
    const teamCount = ref(0);
    const messagesRef = ref(null);
    const inputRef = ref(null);
    const ctxVisible = ref(false);
    const ctxX = ref(0);
    const ctxY = ref(0);
    const ctxMsg = ref(null);
    const editingIdx = ref(null);
    const editingText = ref('');
    const editInputRef = ref(null);
    const stickerOpen = ref(false);
    const readers = ref([]);
    const totalUnread = ref(0);
    const uploading = ref(false);

    let socket = null;
    let unreadTimer = null;


    function getUnreadCount(msg) {
      if (!msg.idx) return 0;
      let count = 0;
      const total = teamCount.value;
      const readList = readers.value;
      for (let i = 0; i < readList.length; i++) {
        if (readList[i].mb_id !== mbId && readList[i].last_read_idx >= msg.idx) count++;
      }
      const unread = Math.max(0, total - 1 - count);
      return unread;
    }

    async function fetchTotalUnread() {
      try {
        const rooms = await sql_query(`SELECT main_group_idx FROM group_member WHERE mb_id = '${mbId}' AND status = 'join'`);
        if (!Array.isArray(rooms) || !rooms.length) { totalUnread.value = 0; return; }
        let total = 0;
        for (const room of rooms) {
          try {
            const readRes = await sql_query(`SELECT last_read_idx FROM group_chat_read WHERE main_group_idx = ${room.main_group_idx} AND mb_id = '${mbId}' LIMIT 1`);
            const lastRead = readRes?.[0]?.last_read_idx || 0;
            const cntRes = await sql_query(`SELECT COUNT(*) as cnt FROM group_chat WHERE main_group_idx = ${room.main_group_idx} AND idx > ${lastRead}`);
            total += cntRes?.[0]?.cnt || 0;
          } catch { /* table not ready */ }
        }
        totalUnread.value = total;
      } catch { totalUnread.value = 0; }
    }

    function toggleWidget() {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        fetchChatRooms();
      } else {
        leaveCurrentRoom();
        currentView.value = 'list';
        stickerOpen.value = false;
      }
    }

    async function fetchChatRooms() {
      try {
        const groups = await sql_query(`
          SELECT gm.main_group_idx, gn.name
          FROM group_member gm
          LEFT JOIN group_node gn ON gm.main_group_idx = gn.idx
          WHERE gm.mb_id = '${mbId}' AND gm.status = 'join'
        `);
        if (!Array.isArray(groups)) { chatRooms.value = []; return; }
        const rooms = [];
        for (const g of groups) {
          let last_message = '', last_msg_type = 'text', last_time = null, unread = 0;
          try {
            const lastRes = await sql_query(`SELECT message, msg_type, created_at FROM group_chat WHERE main_group_idx = ${g.main_group_idx} ORDER BY created_at DESC LIMIT 1`);
            if (lastRes?.[0]) { last_message = lastRes[0].message; last_msg_type = lastRes[0].msg_type || 'text'; last_time = lastRes[0].created_at; }
          } catch { /* group_chat table may not exist yet */ }
          try {
            const readRes = await sql_query(`SELECT last_read_idx FROM group_chat_read WHERE main_group_idx = ${g.main_group_idx} AND mb_id = '${mbId}' LIMIT 1`);
            const lastRead = readRes?.[0]?.last_read_idx || 0;
            const cntRes = await sql_query(`SELECT COUNT(*) as cnt FROM group_chat WHERE main_group_idx = ${g.main_group_idx} AND idx > ${lastRead}`);
            unread = cntRes?.[0]?.cnt || 0;
          } catch { /* tables not ready */ }
          rooms.push({ ...g, last_message, last_msg_type, last_time, unread });
        }
        chatRooms.value = rooms;
      } catch { chatRooms.value = []; }
    }

    async function openRoom(room) {
      activeRoom.value = room;
      currentView.value = 'room';
      loading.value = true;
      stickerOpen.value = false;

      try {
        const countRes = await sql_query(`SELECT COUNT(*) as cnt FROM group_member WHERE main_group_idx=${room.main_group_idx} AND status='join'`);
        teamCount.value = countRes?.[0]?.cnt || 0;
      } catch { teamCount.value = 0; }

      try {
        const readRes = await sql_query(`SELECT mb_id, last_read_idx FROM group_chat_read WHERE main_group_idx=${room.main_group_idx}`);
        readers.value = Array.isArray(readRes) ? readRes : [];
      } catch { readers.value = []; }

      try {
        const history = await sql_query(`
          SELECT gc.*, m.name, m.image
          FROM group_chat gc LEFT JOIN member m ON gc.mb_id = m.mb_id
          WHERE gc.main_group_idx = ${room.main_group_idx}
          ORDER BY gc.created_at ASC LIMIT 200
        `);
        messages.value = Array.isArray(history) ? history : [];
      } catch { messages.value = []; }

      loading.value = false;
      scrollToBottom();
      connectSocket(room.main_group_idx);
      nextTick(() => inputRef.value?.focus());
    }

    function connectSocket(groupIdx) {
      if (socket) { socket.disconnect(); }
      socket = io('https://bodiary.com', { transports: ['websocket'] });
      socket.on('connect', () => {
        socket.emit('joinGroupChat', groupIdx);
        markRead();
      });
      socket.on('newGroupChatMessage', (msg) => {
        messages.value.push(msg);
        scrollToBottom();
        if (msg.mb_id !== mbId) markRead();
      });
      socket.on('editedGroupChatMessage', (data) => {
        const f = messages.value.find(m => m.idx === data.idx);
        if (f) f.message = data.message;
      });
      socket.on('deletedGroupChatMessage', (data) => {
        messages.value = messages.value.filter(m => m.idx !== data.idx);
      });
      socket.on('chatReadUpdate', (data) => {
        if (data.main_group_idx === activeRoom.value?.main_group_idx) {
          readers.value = data.readers;
        }
      });
    }

    function markRead() {
      if (!socket || !activeRoom.value || !messages.value.length) return;
      const lastMsg = messages.value[messages.value.length - 1];
      if (!lastMsg?.idx) return;
      socket.emit('markChatRead', {
        main_group_idx: activeRoom.value.main_group_idx,
        mb_id: mbId,
        last_read_idx: lastMsg.idx
      });
    }

    function leaveCurrentRoom() {
      if (socket && activeRoom.value) {
        socket.emit('leaveGroupChat', activeRoom.value.main_group_idx);
        socket.disconnect();
        socket = null;
      }
      activeRoom.value = null;
      messages.value = [];
      readers.value = [];
    }

    function goBack() {
      leaveCurrentRoom();
      currentView.value = 'list';
      stickerOpen.value = false;
      setTimeout(() => fetchChatRooms(), 300);
    }

    function scrollToBottom() {
      nextTick(() => { if (messagesRef.value) messagesRef.value.scrollTop = messagesRef.value.scrollHeight; });
    }

    function onScroll() { /* future: load more on scroll top */ }

    function sendMessage() {
      const text = inputMsg.value.trim();
      if (!text || !socket || !activeRoom.value) return;
      socket.emit('groupChatMessage', {
        main_group_idx: activeRoom.value.main_group_idx,
        mb_id: mbId, message: text, msg_type: 'text',
        name: member_info.name || member_info.nickname || mbId,
        image: member_info.image || ''
      });
      inputMsg.value = '';
      stickerOpen.value = false;
      inputRef.value?.focus();
    }

    function sendSticker(url) {
      if (!socket || !activeRoom.value) return;
      socket.emit('groupChatMessage', {
        main_group_idx: activeRoom.value.main_group_idx,
        mb_id: mbId, message: url, msg_type: 'sticker',
        name: member_info.name || member_info.nickname || mbId,
        image: member_info.image || ''
      });
      stickerOpen.value = false;
    }

    async function handleFileUpload(e) {
      const file = e.target.files?.[0];
      if (!file || !activeRoom.value) return;
      uploading.value = true;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post('https://bodiary.com/api/chat/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data.success && socket) {
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
          socket.emit('groupChatMessage', {
            main_group_idx: activeRoom.value.main_group_idx,
            mb_id: mbId,
            message: isImage ? '' : file.name,
            msg_type: isImage ? 'image' : 'file',
            file_url: res.data.file_url,
            file_name: res.data.file_name,
            name: member_info.name || member_info.nickname || mbId,
            image: member_info.image || ''
          });
        }
      } catch (err) {
        console.error('파일 업로드 실패:', err);
      }
      uploading.value = false;
      e.target.value = '';
    }

    function onMsgContext(e, msg) {
      if (msg.mb_id !== mbId) return;
      ctxMsg.value = msg;
      ctxX.value = e.clientX;
      ctxY.value = e.clientY;
      ctxVisible.value = true;
    }
    function hideCtx() { ctxVisible.value = false; ctxMsg.value = null; }
    function startEdit() {
      if (!ctxMsg.value) return;
      editingIdx.value = ctxMsg.value.idx;
      editingText.value = ctxMsg.value.message;
      hideCtx();
      nextTick(() => {
        const el = Array.isArray(editInputRef.value) ? editInputRef.value[0] : editInputRef.value;
        el?.focus();
      });
    }
    function confirmEdit() {
      const text = editingText.value.trim();
      if (!text || !socket || !activeRoom.value) { cancelEdit(); return; }
      socket.emit('editGroupChatMessage', { idx: editingIdx.value, main_group_idx: activeRoom.value.main_group_idx, mb_id: mbId, message: text });
      editingIdx.value = null; editingText.value = '';
    }
    function cancelEdit() { editingIdx.value = null; editingText.value = ''; }
    function deleteMsg() {
      if (!ctxMsg.value || !socket || !activeRoom.value) { hideCtx(); return; }
      socket.emit('deleteGroupChatMessage', { idx: ctxMsg.value.idx, main_group_idx: activeRoom.value.main_group_idx, mb_id: mbId });
      hideCtx();
    }
    function onDocClick() { hideCtx(); }

    function formatTime(dt) {
      if (!dt) return '';
      const d = new Date(dt);
      const h = d.getHours(); const m = String(d.getMinutes()).padStart(2, '0');
      return `${h < 12 ? '오전' : '오후'} ${h % 12 || 12}:${m}`;
    }
    function formatDate(dt) {
      if (!dt) return '';
      const d = new Date(dt); const now = new Date(); const diff = now - d;
      if (diff < 60000) return '방금';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
      if (d.toDateString() === now.toDateString()) return formatTime(dt);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    }

    onMounted(() => {
      document.addEventListener('click', onDocClick);
      fetchTotalUnread();
      unreadTimer = setInterval(fetchTotalUnread, 30000);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocClick);
      leaveCurrentRoom();
      if (unreadTimer) clearInterval(unreadTimer);
    });

    return {
      isOpen, currentView, chatRooms, activeRoom, messages, inputMsg, loading, mbId, teamCount,
      messagesRef, inputRef, editInputRef, ctxVisible, ctxX, ctxY, ctxMsg,
      editingIdx, editingText, stickerOpen, totalUnread, uploading, readers,
      toggleWidget, openRoom, goBack, sendMessage, sendSticker, handleFileUpload,
      onMsgContext, startEdit, confirmEdit, cancelEdit, deleteMsg,
      formatTime, formatDate, getUnreadCount, onScroll, window
    };
  }
};
</script>

<style scoped>
.chat-widget-wrap { position: fixed; bottom: 24px; right: 24px; z-index: 9990; }

.chat-fab {
  position: relative; width: 56px; height: 56px; border-radius: 50%; border: none;
  background: linear-gradient(135deg, #6366f1, #818cf8); color: #fff; font-size: 24px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 24px rgba(99,102,241,0.5); transition: all 0.2s ease;
}
.chat-fab:hover { transform: scale(1.08); box-shadow: 0 8px 32px rgba(99,102,241,0.6); }
.chat-fab.active { background: rgba(255,255,255,0.15); box-shadow: none; }
.fab-badge {
  position: absolute; top: -4px; right: -4px; background: #ef4444; color: #fff;
  font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 10px;
  min-width: 18px; text-align: center; line-height: 1.4;
  box-shadow: 0 2px 8px rgba(239,68,68,0.5); animation: badgePulse 2s infinite;
}
@keyframes badgePulse { 0%,100%{ transform: scale(1); } 50%{ transform: scale(1.1); } }

.chat-panel {
  position: absolute; bottom: 68px; right: 0; width: 380px; height: 540px;
  background: rgba(18,18,30,0.97); backdrop-filter: blur(24px); border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 20px 70px rgba(0,0,0,0.5);
  display: flex; flex-direction: column; overflow: hidden;
}
.chat-panel-enter-active,.chat-panel-leave-active { transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1); }
.chat-panel-enter-from,.chat-panel-leave-to { opacity: 0; transform: translateY(20px) scale(0.95); }

.cp-header { display: flex; align-items: center; padding: 16px 18px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.cp-header h3 { margin: 0; font-size: 18px; font-weight: 700; color: #fff; }
.cp-back { background: none; border: none; color: rgba(255,255,255,0.6); font-size: 20px; cursor: pointer; padding: 4px; margin-right: 8px; display: flex; transition: color 0.15s; }
.cp-back:hover { color: #fff; }
.cp-header-info { display: flex; flex-direction: column; }
.cp-header-name { font-size: 15px; font-weight: 600; color: #fff; }
.cp-header-count { font-size: 11px; color: rgba(255,255,255,0.35); }

.cp-list { flex: 1; overflow-y: auto; padding: 8px; }
.cp-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: rgba(255,255,255,0.2); padding: 40px 0; }
.cp-empty > i { font-size: 40px; margin-bottom: 8px; }
.cp-empty > p { font-size: 13px; margin: 0; }

.cp-room-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 14px; cursor: pointer; transition: background 0.15s; }
.cp-room-item:hover { background: rgba(255,255,255,0.06); }
.cp-room-icon { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.3)); display: flex; align-items: center; justify-content: center; color: #a5b4fc; font-size: 20px; flex-shrink: 0; }
.cp-room-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.cp-room-name { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.9); }
.cp-room-last { font-size: 12px; color: rgba(255,255,255,0.35); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cp-room-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
.cp-room-time { font-size: 10px; color: rgba(255,255,255,0.25); }
.cp-room-badge { background: #ef4444; color: #fff; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px; min-width: 18px; text-align: center; }

.cp-messages { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 4px; }
.cp-loading { text-align: center; color: rgba(255,255,255,0.3); padding: 20px; font-size: 13px; }
.cp-empty-msg { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: rgba(255,255,255,0.2); }
.cp-empty-msg > i { font-size: 40px; margin-bottom: 8px; }
.cp-empty-msg > p { font-size: 13px; margin: 0; }

.cp-msg { display: flex; gap: 8px; align-items: flex-end; max-width: 82%; }
.cp-msg.mine { align-self: flex-end; flex-direction: row-reverse; }
.cp-msg-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.cp-msg-avatar-spacer { width: 28px; flex-shrink: 0; }
.cp-msg-body { display: flex; flex-direction: column; gap: 2px; }
.cp-msg.mine .cp-msg-body { align-items: flex-end; }
.cp-msg-name { font-size: 11px; color: rgba(255,255,255,0.35); margin-left: 4px; }

.cp-msg-bubble {
  padding: 9px 13px; border-radius: 16px; font-size: 13px; line-height: 1.45;
  word-break: break-word; background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.9);
  border-bottom-left-radius: 5px; cursor: default;
}
.cp-msg.mine .cp-msg-bubble { background: rgba(99,102,241,0.65); color: #fff; border-bottom-left-radius: 16px; border-bottom-right-radius: 5px; }

.cp-msg-sticker { padding: 4px; }
.cp-msg-sticker img { width: 100px; height: 100px; object-fit: contain; }
.cp-msg-img { padding: 2px; cursor: pointer; }
.cp-msg-img img { max-width: 180px; max-height: 160px; border-radius: 12px; object-fit: cover; }
.cp-msg-file {
  display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 14px;
  background: rgba(255,255,255,0.06); cursor: pointer; transition: background 0.15s;
}
.cp-msg-file:hover { background: rgba(255,255,255,0.12); }
.cp-msg-file i { font-size: 20px; color: #a5b4fc; }
.cp-msg-file span { font-size: 12px; color: rgba(255,255,255,0.8); max-width: 140px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.cp-msg-meta { display: flex; align-items: center; gap: 4px; }
.cp-msg.mine .cp-msg-meta { flex-direction: row-reverse; }
.cp-msg-read { font-size: 9px; color: #a5b4fc; font-weight: 600; }
.cp-msg-time { font-size: 9px; color: rgba(255,255,255,0.18); }

.cp-msg-edit-wrap { display: flex; align-items: center; gap: 4px; }
.cp-msg-edit-input { padding: 8px 12px; border-radius: 14px; font-size: 13px; border: 1px solid rgba(99,102,241,0.5); background: rgba(255,255,255,0.06); color: #fff; outline: none; min-width: 120px; }
.cp-msg-edit-actions { display: flex; gap: 2px; }
.cp-msg-edit-actions button { background: none; border: none; color: rgba(255,255,255,0.5); font-size: 16px; cursor: pointer; padding: 4px; display: flex; }
.cp-msg-edit-actions button:hover { color: #fff; }


.cp-input-area { display: flex; gap: 6px; padding: 10px 12px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(0,0,0,0.15); align-items: center; }
.cp-tool-btn {
  width: 36px; height: 36px; border-radius: 50%; border: none; background: none;
  color: rgba(255,255,255,0.4); font-size: 18px; cursor: pointer; display: flex;
  align-items: center; justify-content: center; transition: all 0.15s; flex-shrink: 0;
}
.cp-tool-btn:hover,.cp-tool-btn.active { color: #a5b4fc; background: rgba(99,102,241,0.15); }
.cp-input { flex: 1; padding: 10px 14px; border-radius: 20px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.05); color: #fff; font-size: 13px; outline: none; transition: border-color 0.2s; min-width: 0; }
.cp-input:focus { border-color: rgba(99,102,241,0.5); }
.cp-input::placeholder { color: rgba(255,255,255,0.2); }
.cp-send { width: 36px; height: 36px; border-radius: 50%; border: none; background: rgba(99,102,241,0.8); color: #fff; font-size: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.15s, transform 0.1s; }
.cp-send:hover { background: rgba(99,102,241,1); transform: scale(1.05); }
.cp-send:disabled { opacity: 0.3; cursor: default; transform: none; }

.ctx-menu { position: fixed; z-index: 99999; background: rgba(25,25,40,0.97); backdrop-filter: blur(16px); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.5); padding: 4px; min-width: 120px; }
.ctx-menu button { display: flex; align-items: center; gap: 8px; width: 100%; padding: 10px 14px; border: none; background: none; color: rgba(255,255,255,0.8); font-size: 13px; cursor: pointer; border-radius: 8px; transition: background 0.12s; }
.ctx-menu button:hover { background: rgba(255,255,255,0.08); }
.ctx-menu button.ctx-danger { color: #f87171; }
.ctx-menu button.ctx-danger:hover { background: rgba(248,113,113,0.12); }
.ctx-fade-enter-active,.ctx-fade-leave-active { transition: all 0.12s ease; }
.ctx-fade-enter-from,.ctx-fade-leave-to { opacity: 0; transform: scale(0.95); }

@media (max-width: 580px) {
  .chat-widget-wrap { bottom: 16px; right: 16px; }
  .chat-fab { width: 48px; height: 48px; font-size: 20px; }
  .chat-panel { width: calc(100vw - 32px); height: 70dvh; bottom: 60px; right: 0; border-radius: 16px; }
}
</style>
